// The form a customer, supplier, job worker or Item is added or corrected with (#502): on the Customers and suppliers
// and Items and stock pages, and from a draft LAMP read in chat. The API checks every value (a GSTIN's check
// character, its State, an HSN's digits, an Item's GST rate) and answers what is wrong in plain words, shown under the form.
import { useState, type FormEvent } from "react";
import {
  ITEM_KINDS,
  type CounterpartyKind,
  type ItemKind,
  type MasterDataFields,
  type MasterDataRecord,
} from "../api.js";
import { ApiError } from "../http.js";
import { useWords, type Words } from "../i18n/words.js";

const ITEM_KIND_WORDS: Readonly<Record<ItemKind, (w: Words) => string>> = {
  finished_part: (w) => w.masterFinishedPart,
  raw_material: (w) => w.masterRawMaterial,
  consumable: (w) => w.masterConsumable,
};

interface Values {
  kind: CounterpartyKind;
  name: string;
  gstin: string;
  address: string;
  state: string;
  phone: string;
  email: string;
  terms: string;
  itemKind: ItemKind | "";
  unit: string;
  hsn: string;
  gstRate: string;
  supplier: string;
  opening: string;
  openingUnit: string;
}

const valuesOf = (record: MasterDataRecord, initial: MasterDataFields): Values => ({
  kind: record === "item" ? "customer" : record,
  name: initial.name ?? "",
  gstin: initial.gstin ?? "",
  address: initial.address ?? "",
  state: initial.state ?? "",
  phone: initial.phone ?? "",
  email: initial.email ?? "",
  terms: initial.paymentTermsDays === undefined ? "" : String(initial.paymentTermsDays),
  itemKind: initial.itemKind ?? "",
  unit: initial.unit ?? "",
  hsn: initial.hsn ?? "",
  gstRate: initial.gstRate === undefined ? "" : String(initial.gstRate),
  supplier: initial.supplier ?? "",
  opening: initial.openingStock === undefined ? "" : String(initial.openingStock.quantity),
  // A drafted opening stock keeps the unit it was said in, so a correction never recounts it in the Item's unit.
  openingUnit: initial.openingStock?.unit ?? "",
});

/** A GST rate as typed, "18" or "18%", as a number; undefined when none is typed. */
const RATE = /^\s*(\d{1,3}(?:\.\d{1,2})?)\s*%?\s*$/;
const rateOf = (typed: string) => {
  const rate = RATE.exec(typed)?.[1];
  return rate === undefined ? undefined : Number(rate);
};

/** The fields given, as the API takes them: empty ones left out, so a correction keeps what is on record. */
function fieldsOf(record: MasterDataRecord, v: Values, editing: boolean): MasterDataFields & { name: string } {
  const given = (value: string) => (value.trim() === "" ? undefined : value.trim());
  const pick = (entries: [string, unknown][]) =>
    Object.fromEntries(entries.filter(([, value]) => value !== undefined)) as MasterDataFields;
  if (record !== "item")
    return {
      name: v.name.trim(),
      ...pick([
        ["gstin", given(v.gstin)?.toUpperCase()],
        ["address", given(v.address)],
        ["state", given(v.state)],
        ["phone", given(v.phone)],
        ["email", given(v.email)],
        ["paymentTermsDays", given(v.terms) === undefined ? undefined : Number(v.terms)],
      ]),
    };
  const opening = given(v.opening);
  const openingUnit = given(v.openingUnit);
  return {
    name: v.name.trim(),
    ...pick([
      ["itemKind", v.itemKind === "" ? undefined : v.itemKind],
      ["unit", editing ? undefined : given(v.unit)],
      ["hsn", given(v.hsn)],
      ["gstRate", rateOf(v.gstRate)],
      ["supplier", given(v.supplier)],
      [
        "openingStock",
        opening === undefined
          ? undefined
          : { quantity: Number(opening), ...(openingUnit === undefined ? {} : { unit: openingUnit }) },
      ],
    ]),
  };
}

/** The fields the form shows for `record`, by the name the API takes them under. */
function shownFields(record: MasterDataRecord, editing: boolean): (keyof MasterDataFields)[] {
  return record === "item"
    ? ["itemKind", ...(editing ? [] : (["unit"] as const)), "hsn", "gstRate", "supplier", "openingStock"]
    : ["gstin", "address", "state", "phone", "email", "paymentTermsDays"];
}

/** The fields the form shows that the person left empty: on a draft, each is cleared rather than kept. */
function emptiedFields(record: MasterDataRecord, v: Values, editing: boolean): (keyof MasterDataFields)[] {
  const given = fieldsOf(record, v, editing);
  return shownFields(record, editing).filter((field) => given[field] === undefined);
}

export function MasterDataForm({
  record,
  initial = {},
  editing = false,
  chooseKind = false,
  submitLabel,
  onSubmit,
}: {
  record: MasterDataRecord;
  initial?: MasterDataFields;
  /** Correcting one on record: an Item's unit is the stock Ledger's and is not changed. */
  editing?: boolean;
  /** A new Counterparty: the person says which kind it is. */
  chooseKind?: boolean;
  submitLabel: string;
  /**
   * Saves the fields, as a Counterparty of `kind` when it is one; answers what LAMP did, in words. `emptied` names the
   * fields shown that the person left empty.
   */
  onSubmit: (
    fields: MasterDataFields & { name: string },
    kind: CounterpartyKind,
    emptied: readonly (keyof MasterDataFields)[],
  ) => Promise<string | undefined>;
}) {
  const w = useWords();
  const [values, setValues] = useState(() => valuesOf(record, initial));
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string>();
  const [problem, setProblem] = useState<string>();
  const set = (key: keyof Values) => (event: { target: { value: string } }) =>
    setValues({ ...values, [key]: event.target.value });
  const party = record !== "item";
  const ready =
    values.name.trim() !== "" &&
    (values.terms.trim() === "" || /^\d{1,4}$/.test(values.terms.trim())) &&
    (values.opening.trim() === "" || /^\d+(\.\d{1,6})?$/.test(values.opening.trim())) &&
    (values.gstRate.trim() === "" || RATE.test(values.gstRate)) &&
    (party || editing || values.unit.trim() !== "");

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setNotice(undefined);
    setProblem(undefined);
    try {
      setNotice(await onSubmit(fieldsOf(record, values, editing), values.kind, emptiedFields(record, values, editing)));
    } catch (error) {
      setProblem(error instanceof ApiError && error.problems.length > 0 ? error.problems.join("; ") : w.masterFailed);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={(event) => void submit(event)}>
      {party && chooseKind ? (
        <label className="field">
          {w.masterKind}{" "}
          <select value={values.kind} onChange={set("kind")}>
            <option value="customer">{w.partiesCustomer}</option>
            <option value="supplier">{w.partiesSupplier}</option>
            <option value="job_worker">{w.partiesJobWorker}</option>
          </select>
        </label>
      ) : null}
      <label className="field">
        {w.masterName} <input value={values.name} maxLength={500} translate="no" onChange={set("name")} />
      </label>
      {party ? (
        <>
          <label className="field">
            {w.masterGstin} <input value={values.gstin} maxLength={15} autoComplete="off" onChange={set("gstin")} />
          </label>
          <label className="field">
            {w.masterAddress}{" "}
            <textarea value={values.address} maxLength={500} rows={2} translate="no" onChange={set("address")} />
          </label>
          <label className="field">
            {w.masterState} <input value={values.state} maxLength={100} onChange={set("state")} />
          </label>
          <label className="field">
            {w.masterPhone} <input value={values.phone} maxLength={30} inputMode="tel" onChange={set("phone")} />
          </label>
          <label className="field">
            {w.masterEmail} <input value={values.email} maxLength={254} inputMode="email" onChange={set("email")} />
          </label>
          <label className="field">
            {w.masterTerms} <input value={values.terms} maxLength={4} inputMode="numeric" onChange={set("terms")} />
          </label>
        </>
      ) : (
        <>
          <label className="field">
            {w.masterItemKind}{" "}
            <select value={values.itemKind} onChange={set("itemKind")}>
              <option value="">{w.masterItemKindNone}</option>
              {ITEM_KINDS.map((kind) => (
                <option key={kind} value={kind}>
                  {ITEM_KIND_WORDS[kind](w)}
                </option>
              ))}
            </select>
          </label>
          {editing ? null : (
            <label className="field">
              {w.masterUnit} <input value={values.unit} maxLength={20} onChange={set("unit")} />
            </label>
          )}
          <label className="field">
            {w.masterHsn} <input value={values.hsn} maxLength={8} inputMode="numeric" onChange={set("hsn")} />
          </label>
          <label className="field">
            {w.masterGstRate}{" "}
            <input value={values.gstRate} maxLength={7} inputMode="decimal" onChange={set("gstRate")} />
            <span className="small muted"> {w.masterGstRateHelp}</span>
          </label>
          <label className="field">
            {w.masterSupplier}{" "}
            <input value={values.supplier} maxLength={500} translate="no" onChange={set("supplier")} />
          </label>
          <label className="field">
            {w.masterOpening} <input value={values.opening} inputMode="decimal" onChange={set("opening")} />
            <span className="small muted"> {w.masterOpeningHelp}</span>
          </label>
          <label className="field">
            {w.masterOpeningUnit} <input value={values.openingUnit} maxLength={20} onChange={set("openingUnit")} />
          </label>
        </>
      )}
      <button type="submit" className="btn" disabled={busy || !ready}>
        {submitLabel}
      </button>
      {notice ? <p role="status">{notice}</p> : null}
      {problem ? (
        <p className="alert" role="alert">
          {problem}
        </p>
      ) : null}
    </form>
  );
}
